import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { BATCH_KIND_LABEL, type BatchKind, type BatchStatus, type ItemStatus } from '@ia/shared';
import { bucketOf, formatDate, relative, STATUS_LABEL, useNow } from '../util';

export function StatusPill({ status }: { status: BatchStatus }) {
  return <span className={`pill pill-${bucketOf(status)}`}>{STATUS_LABEL[status] ?? status}</span>;
}

/** What the batch carries: policies from a portal or policy file, or client records from a CRM export. */
export function KindTag({ kind }: { kind: BatchKind }) {
  return (
    <span
      className={`tag tag-${kind}`}
      title={kind === 'insurance' ? 'Insurance policies captured from a carrier portal or a policy file' : 'Client (customer) records imported from a CRM export'}
    >
      {BATCH_KIND_LABEL[kind]}
    </span>
  );
}

const ITEM_TONE: Record<string, string> = {
  pending: 'pending',
  skipped: 'cancelled',
  succeeded: 'processed',
  failed: 'failed',
  running: 'pending',
  queued: 'pending',
};

export function ItemPill({ status }: { status: ItemStatus | 'running' | 'queued' }) {
  return <span className={`pill pill-${ITEM_TONE[status] ?? 'cancelled'}`}>{STATUS_LABEL[status] ?? status}</span>;
}

export function When({ iso, inline }: { iso: string | null | undefined; inline?: boolean }) {
  const now = useNow();
  if (!iso) return <span className="muted">—</span>;
  return (
    <time dateTime={iso} title={new Date(iso).toISOString()} className={inline ? 'when inline' : 'when'}>
      <span>{formatDate(iso)}</span>
      <span className="muted small">{relative(iso, now)}</span>
    </time>
  );
}

export function Alert({ kind = 'error', children }: { kind?: 'error' | 'warn' | 'info' | 'ok'; children: ReactNode }) {
  return (
    <div className={`alert alert-${kind}`} role={kind === 'error' ? 'alert' : 'status'}>
      {children}
    </div>
  );
}

export function Field({ label, hint, children }: { label: string; hint?: ReactNode; children: (id: string) => ReactNode }) {
  const id = useId();
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {children(id)}
      {hint && <div className="hint">{hint}</div>}
    </div>
  );
}

export function Spinner({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="loading" role="status">
      <span className="spinner" aria-hidden="true" />
      {label}
    </div>
  );
}

/**
 * Modal dialog on top of native <dialog>: focus trap, Escape to close and
 * focus restore come from the browser.
 */
export function Dialog({
  open,
  title,
  onClose,
  children,
  footer,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      className="dialog"
      aria-labelledby={titleId}
      onClose={onClose}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
    >
      {open && (
        <div className="dialog-body">
          <h2 id={titleId}>{title}</h2>
          <div className="dialog-content">{children}</div>
          <div className="dialog-footer">{footer}</div>
        </div>
      )}
    </dialog>
  );
}

export function ConfirmDialog({
  open,
  title,
  children,
  confirmLabel,
  danger,
  busy,
  error,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  children: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  busy?: boolean;
  error?: string | null;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Dialog
      open={open}
      title={title}
      onClose={() => !busy && onClose()}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className={danger ? 'btn btn-danger' : 'btn btn-primary'} onClick={onConfirm} disabled={busy} autoFocus>
            {busy ? 'Working…' : confirmLabel}
          </button>
        </>
      }
    >
      {children}
      {error && <Alert>{error}</Alert>}
    </Dialog>
  );
}

export function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="btn"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
        } catch {
          const ta = document.createElement('textarea');
          ta.value = text;
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          ta.remove();
        }
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      {copied ? 'Copied' : label}
    </button>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}
